import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  CircleUserRound,
  Info,
  Mail,
  MapPin,
  Phone,
  Plus,
  Trash2,
  User,
  VenusAndMars,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useFormSuccess } from "@/shared/hooks/useFormSuccess";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { EmployeeAvatar } from "../components/EmployeeAvatar";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { provinceOptions } from "../data";
import {
  createCompanyEmployee,
  fetchCompanyHospitals,
  type CreateCompanyEmployeePayload,
} from "../api/companyApi";
import {
  createAdminEmployee,
  fetchAdminEmployeeMeta,
  fetchAdminNetworkHospitals,
} from "../api/adminApi";
import { EmployeeNetworkHospitalsField } from "../components/EmployeeNetworkHospitalsField";
import { EMPLOYEE_POSITION_OPTIONS } from "../lib/employeePositions";
import { getAuthToken, getStoredAccountType } from "../auth";
import { FAMILY_RELATIONSHIP_OPTIONS } from "../lib/familyRelationships";

type DependentForm = {
  id: number;
  name: string;
  relationship: string;
  gender: string;
  birthdate: string;
  photoFile?: File | null;
};

const emptyDependent = (id: number): DependentForm => ({
  id,
  name: "",
  relationship: "",
  gender: "",
  birthdate: "",
  photoFile: null,
});

export function AddEmployeePage() {
  const { t } = useTranslation();
  const labels = t.safecheck.employees.create;
  const saveSuccess = useFormSuccess();
  const { showToast } = usePageActions();

  const isAdmin = getStoredAccountType() === "admin";
  const [employeeName, setEmployeeName] = useState("");
  const [matriculeCompany, setMatriculeCompany] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [gender, setGender] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedHospitalIds, setSelectedHospitalIds] = useState<number[]>([]);
  const [primaryHospitalId, setPrimaryHospitalId] = useState<number | null>(null);
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [hospitals, setHospitals] = useState<Array<{ id: number; name: string }>>([]);
  const [dependents, setDependents] = useState<DependentForm[]>([]);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreview(null);
      return;
    }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  useEffect(() => {
    if (!getAuthToken()) return;

    let cancelled = false;
    void (async () => {
      try {
        if (isAdmin) {
          const meta = await fetchAdminEmployeeMeta();
          if (!cancelled) setCompanies(meta.companies ?? []);
          return;
        }
        const rows = await fetchCompanyHospitals();
        if (!cancelled) setHospitals(rows);
      } catch (e) {
        if (!cancelled) {
          showToast({
            message:
              e instanceof ApiError
                ? e.message
                : "Impossible de charger les entreprises / hôpitaux.",
            variant: "danger",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, showToast]);

  useEffect(() => {
    if (!isAdmin || !companyId || !getAuthToken()) {
      if (isAdmin) {
        setHospitals([]);
        setSelectedHospitalIds([]);
        setPrimaryHospitalId(null);
      }
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchAdminNetworkHospitals(Number(companyId));
        if (cancelled) return;
        setHospitals(rows);
        setSelectedHospitalIds([]);
        setPrimaryHospitalId(null);
      } catch {
        if (!cancelled) {
          setHospitals([]);
          setSelectedHospitalIds([]);
          setPrimaryHospitalId(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, companyId]);

  const updateDependent = (id: number, patch: Partial<DependentForm>) => {
    setDependents((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const handleSubmit = async () => {
    if (!employeeName.trim() || !matriculeCompany.trim() || !birthdate || !gender) {
      showToast({ message: "Nom, matricule entreprise, date de naissance et sexe sont obligatoires.", variant: "warning" });
      return;
    }

    if (isAdmin && !companyId) {
      showToast({ message: "Sélectionnez une entreprise.", variant: "warning" });
      return;
    }

    if (!getAuthToken()) {
      saveSuccess("/dashboard/employees", t.actions.saved);
      return;
    }

    const hospitalPayload = {
      hospital_ids: selectedHospitalIds,
      primary_hospital_id: primaryHospitalId ?? undefined,
      hospital_id: primaryHospitalId ?? selectedHospitalIds[0] ?? undefined,
    };

    const payload: CreateCompanyEmployeePayload = {
      employee_name: employeeName.trim(),
      matricule_company: matriculeCompany.trim(),
      birthdate,
      gender: gender as "M" | "F",
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      city: city.trim() || undefined,
      province: province || undefined,
      position: position || undefined,
      ...hospitalPayload,
      family_members: dependents
        .filter((row) => row.name.trim())
        .map((row) => ({
          name: row.name.trim(),
          relationship: row.relationship || undefined,
          birthdate: row.birthdate || undefined,
          gender: row.gender === "M" || row.gender === "F" ? row.gender : undefined,
        })),
    };

    const familyPhotos = dependents.filter((row) => row.name.trim()).map((row) => row.photoFile ?? null);

    setSaving(true);
    try {
      if (isAdmin) {
        await createAdminEmployee(
          { ...payload, company_id: Number(companyId) },
          photoFile,
          familyPhotos,
        );
      } else {
        await createCompanyEmployee(payload, photoFile, familyPhotos);
      }
      saveSuccess("/dashboard/employees", t.actions.saved);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Création de l'employé impossible.";
      showToast({ message, variant: "warning" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.employees, href: "/dashboard/employees" },
          { label: labels.title },
        ]}
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/employees"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={14} />
          {labels.backToList}
        </Link>
      </div>

      <div className="theme-card p-6">
        <h1 className="mb-6 text-xl font-semibold text-foreground">{labels.cardTitle}</h1>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label={labels.fields.fullName} required>
            <div className="relative">
              <User size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                placeholder={labels.placeholders.fullName}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>
          <FormField label={labels.fields.companyMatricule} required>
            <div className="relative">
              <Building2 size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={matriculeCompany}
                onChange={(e) => setMatriculeCompany(e.target.value)}
                placeholder={labels.placeholders.companyMatricule}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.birthDate} required>
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="date"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>
          <FormField label={labels.fields.gender} required>
            <div className="relative">
              <VenusAndMars size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className={`${selectClass} pl-9`}
              >
                <option value="">{labels.placeholders.gender}</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </div>
          </FormField>

          {isAdmin ? (
            <FormField label="Entreprise" required>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className={selectClass}
              >
                <option value="">— Sélectionner —</option>
                {companies.map((company) => (
                  <option key={company.id} value={String(company.id)}>
                    {company.name}
                  </option>
                ))}
              </select>
            </FormField>
          ) : null}

          <EmployeeNetworkHospitalsField
            hospitals={hospitals}
            selectedIds={selectedHospitalIds}
            primaryId={primaryHospitalId}
            emptyHint={
              isAdmin && !companyId
                ? "Sélectionnez d'abord une entreprise pour charger le réseau d'hôpitaux."
                : "Aucun hôpital disponible — vérifiez les conventions actives de l'entreprise."
            }
            onChange={({ selectedIds, primaryId }) => {
              setSelectedHospitalIds(selectedIds);
              setPrimaryHospitalId(primaryId);
            }}
          />

          <FormField label={labels.fields.position}>
            <select value={position} onChange={(e) => setPosition(e.target.value)} className={selectClass}>
              <option value="">{labels.placeholders.position}</option>
              {EMPLOYEE_POSITION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.fields.phone}>
            <div className="relative">
              <Phone size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={labels.placeholders.phone}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.email}>
            <div className="relative">
              <Mail size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={labels.placeholders.email}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>
          <FormField label={labels.fields.city}>
            <div className="relative">
              <MapPin size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={labels.placeholders.city}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.province}>
            <select value={province} onChange={(e) => setProvince(e.target.value)} className={selectClass}>
              <option value="">{labels.placeholders.province}</option>
              {provinceOptions
                .filter((option) => option.value)
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </FormField>
        </div>

        <div className="mt-6 rounded-xl border border-border p-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Photo de l'employé
          </p>
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-lg border border-border bg-card-muted/40">
              {photoPreview ? (
                <img src={photoPreview} alt="" className="h-full w-full object-cover" />
              ) : employeeName.trim() ? (
                <EmployeeAvatar name={employeeName} size={112} rounded="lg" />
              ) : (
                <CircleUserRound size={42} className="text-muted" />
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  setPhotoFile(file);
                }}
                className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-card-muted file:px-3 file:py-1.5 file:text-sm file:text-foreground"
              />
              <p className="mt-1.5 flex items-center gap-1 text-xs text-muted">
                <Info size={12} />
                JPEG, PNG ou WebP — 2 Mo max. Optionnel.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-border p-4">
          <p className="text-base font-semibold text-foreground">
            Ayant droit <span className="text-sm font-normal text-muted">(Optionnel)</span>
          </p>
          <div className="mt-3 rounded-lg bg-teal-soft px-3 py-2 text-sm text-teal">
            L'ajout d'ayant droit n'est pas obligatoire. Vous pouvez les ajouter plus tard.
          </div>
          {dependents.map((dependent, index) => (
            <div key={dependent.id} className="mt-3 rounded-lg border border-border p-3">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
                <div className="lg:col-span-2">
                  <p className="text-sm font-medium text-foreground">Ayant droit {index + 1}</p>
                  <button
                    type="button"
                    onClick={() => setDependents((prev) => prev.filter((item) => item.id !== dependent.id))}
                    className="mt-2 inline-flex items-center gap-2 rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-white hover:bg-danger/90"
                  >
                    <Trash2 size={14} />
                    Supprimer
                  </button>
                </div>

                <div className="lg:col-span-3">
                  <FormField label="NOM COMPLET">
                    <div className="relative">
                      <User size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                      <input
                        value={dependent.name}
                        onChange={(e) => updateDependent(dependent.id, { name: e.target.value })}
                        placeholder="Ex: Jean Dupont"
                        className={`${inputClass} pl-9`}
                      />
                    </div>
                  </FormField>
                </div>

                <div className="lg:col-span-2">
                  <FormField label="RELATION">
                    <select
                      value={dependent.relationship}
                      onChange={(e) => updateDependent(dependent.id, { relationship: e.target.value })}
                      className={selectClass}
                    >
                      <option value="">—</option>
                      {FAMILY_RELATIONSHIP_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </FormField>
                </div>

                <div className="lg:col-span-2">
                  <FormField label="SEXE">
                    <select
                      value={dependent.gender}
                      onChange={(e) => updateDependent(dependent.id, { gender: e.target.value })}
                      className={selectClass}
                    >
                      <option value="">--</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </select>
                  </FormField>
                </div>

                <div className="lg:col-span-2">
                  <FormField label="DATE DE NAISSANCE">
                    <input
                      type="date"
                      value={dependent.birthdate}
                      onChange={(e) => updateDependent(dependent.id, { birthdate: e.target.value })}
                      className={inputClass}
                    />
                  </FormField>
                </div>

                <div className="lg:col-span-3">
                  <FormField label="PHOTO">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      onChange={(e) =>
                        updateDependent(dependent.id, {
                          photoFile: e.target.files?.[0] ?? null,
                        })
                      }
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm file:mr-2 file:rounded-md file:border-0 file:bg-card-muted file:px-2 file:py-1"
                    />
                  </FormField>
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setDependents((prev) => [...prev, emptyDependent(Date.now() + Math.floor(Math.random() * 1000))])
            }
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <Plus size={15} />
            Ajouter un ayant droit
          </button>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Link
            to="/dashboard/employees"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            {t.safecheck.back}
          </Link>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSubmit()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : "Enregistrer l'employé"}
          </button>
        </div>
      </div>
    </>
  );
}
